// InfoBaumpflege.js – Leistungsseite /baumpflege: Hero, Fachtexte, Karussell, Verweise auf andere Leistungen.
import "./Home.css";
import "./custom.css";
import usePageMeta from "../../usePageMeta";
import { JsonLd, leistungSchema } from "../Strukturdaten";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Carousel from "react-bootstrap/Carousel";
import { karussellIntervall } from "../../reducedMotion";

import Klettertechnik from "./images/Arbeiten/Klettertechnik.webp";
import Buehne from "./images/Arbeiten/Buehne.webp";
import Buehne2 from "./images/Arbeiten/verkehrssicherung-buehne.webp";

const facts = [
  {
    title: "Bis zu 500 Insektenarten",
    text: "Eine einzelne alte Eiche kann über 500 verschiedene Insektenarten beherbergen, mehr als jede andere einheimische Baumart. Viele davon sind ausschließlich auf lebende oder absterbende Eichen spezialisiert.",
  },
  {
    title: "Das CODIT-Prinzip",
    text: "Bäume heilen Wunden nicht, sie schotten sie ab. Das Compartmentalization Of Decay In Trees (CODIT) beschreibt, wie Bäume chemische und anatomische Barrieren bilden, um Fäulnis einzugrenzen. Korrekte Schnittführung am Astkragen respektiert diese Mechanismen.",
  },
  {
    title: "Totholz: Mangelware im Wirtschaftswald",
    text: "Naturnahe mitteleuropäische Wälder enthielten früher deutlich mehr Totholz als die heutigen, intensiv bewirtschafteten Forste. Über 1.300 Käferarten, Hunderte Pilzarten und zahlreiche Höhlenbrüter sind darauf angewiesen.",
  },
  {
    title: "Höhlen brauchen Zeit",
    text: "Spechthöhlen werden im ersten Jahr vom Specht selbst genutzt. In den Folgejahren übernehmen Fledermäuse, Hohltauben oder Siebenschläfer. Wird der Baum zu früh entnommen, geht diese Entwicklungskette unwiederbringlich verloren.",
  },
  {
    title: "Baumwurzeln und der Druckbereich",
    text: "Der Kronentraufbereich markiert grob den unterirdischen Wurzelraum. Verdichtungen durch Befahren schädigen das feine Saugwurzelsystem dauerhaft, oft mit Symptomen, die erst Jahre später als Kronenverlichtung sichtbar werden.",
  },
  {
    title: "Klimawandel und Baumanpassung",
    text: "Vitale, fachgerecht gepflegte Bäume sind deutlich widerstandsfähiger gegenüber Trockenstress, Sturmwurf und Pathogenen. Sachgemäße Kronenpflege reduziert Angriffsflächen für Wind und verringert das Risiko von Kronenversagen.",
  },
];

// Strukturdaten dieser Leistung; nur Inhalte, die unten auf der Seite stehen
const schema = leistungSchema({
  pfad: "/baumpflege",
  name: "Baumpflege",
  serviceType: "Baumpflege",
  beschreibung:
    "Naturnahe Baumpflege: Zustandsbeurteilung, Kronenpflege mit Schnitt am Astkragen nach dem CODIT-Prinzip, " +
    "Verkehrssicherungsschnitt und Erhalt von Habitatbäumen und Totholz, wo die Verkehrssicherheit es zulässt. " +
    "Ausführung mit Seiltechnik oder von der Arbeitsbühne, Eingriffe unter Beachtung von § 39 und § 44 BNatSchG; " +
    "anfallende Äste werden auf Wunsch vor Ort gehäckselt.",
  leistungen: [
    "Zustandsbeurteilung",
    "Kronenpflege",
    "Verkehrssicherungsschnitt",
    "Habitatbaumkonzept: Erhalt von Totholz und Habitatbäumen",
    "Kronenpflege mit Seiltechnik",
    "Kronenarbeit von der Arbeitsbühne",
  ],
});

function InfoBaumpflege() {
  usePageMeta({
    path: "/baumpflege",
    title: "Baumpflege: naturnahe Kronenpflege & Habitatschutz | Forst & Flur",
    description:
      "Baumpflege nach dem CODIT-Prinzip: Kronenpflege, Baumkontrolle und Erhalt von Habitatbäumen in Schleswig-Holstein mit ökologischem Ansatz. Jetzt anfragen.",
  });

  return (
    <div>
      <JsonLd daten={schema} />
      {/* Hero */}
      <div className="ff-page-hero">
        <div className="ff-page-hero__content">
          <span className="ff-eyebrow">Forst &amp; Flur</span>
          <h1 className="ff-page-hero__title">Baumpflege</h1>
          <p className="ff-page-hero__sub">
            Der Baum als Lebensraum, schonend gepflegt, nachhaltig erhalten.
          </p>
        </div>
      </div>

      {/* Artikel-Body */}
      <div className="custom-dark-green text-white">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>

              <h2 className="ff-article-h2">
                Der Baum als Ökosystem:<br />mehr als nur Holz
              </h2>

              <p className="fs-5 text-shadow">
                Ein alter Baum ist eines der komplexesten Ökosysteme auf engstem Raum.
                Borke, Höhlungen, Totäste, Pilzgeflechte und lebende Krone bilden zusammen
                ein vielschichtiges Habitat für Hunderte von Tier-, Pilz- und Flechtenarten,
                viele davon hochgradig spezialisiert und auf genau diese Strukturen angewiesen.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={5}>
                  <img
                    src={Klettertechnik}
                    width={660}
                    height={371}
                    alt="Baumkletterer mit Helm und Klettergurt in der Krone eines Laubbaums"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Baumkletterer bei der Kronenpflege mit Seiltechnik
                  </p>
                </Col>
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Professionelle Baumpflege beginnt mit einer differenzierten Zustandsbeurteilung:
                    Was muss aus Gründen der Verkehrssicherheit wirklich entfernt werden und was
                    sind natürliche Alterungsprozesse, die ökologisch wertvolle Strukturen hervorbringen?
                    Dieser Unterschied erfordert arboristisches Fachwissen und ein Verständnis für
                    die Ökologie des Standorts.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Die Entscheidung, einen Baum zu fällen statt ihn zu pflegen, hat deshalb stets
                    ökologische Konsequenzen, die weit über das sichtbare Holzvolumen hinausgehen.
                    Wir stellen diese Frage konsequent und erklären Ihnen den Unterschied.
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">
                Das CODIT-Prinzip: Wie Bäume auf Wunden reagieren
              </h3>
              <p className="fs-5 text-shadow">
                Anders als Tiere heilen Bäume Wunden nicht, sie schotten sie ab.
                Das als CODIT (Compartmentalization Of Decay In Trees) bekannte Prinzip
                beschreibt, wie Bäume nach Verletzungen chemische und anatomische Barrieren
                ausbilden, um Fäulnis räumlich zu begrenzen.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Ein Schnitt am natürlichen Astkragen bewahrt diese bauminternen Schutzbarrieren.
                    Ein zu flacher oder tiefer Schnitt durchtrennt sie und öffnet Fäulniserregern
                    breite Eintrittspforten. Unsere Baumpfleger orientieren sich konsequent an diesen
                    biologischen Grundlagen, nicht an pauschalen Rückschnittmaßen.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Das Ziel ist stets die langfristige Vitalität des Baumes, nicht eine bestimmte
                    Kronenform oder ein bestimmtes Volumen. Was dem Baum nützt, nützt auch dem
                    Ökosystem, das er trägt.
                  </p>
                </Col>
                <Col xs={12} md={5}>
                  <img
                    src={Buehne}
                    width={660}
                    height={371}
                    alt="Baumpfleger auf einer Hubarbeitsbühne in der kahlen Krone eines großen Laubbaums"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Arbeitsbühne für präzise Kronenarbeit ohne Druck auf den Wurzelraum
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">
                Habitatbäume, Totholz und saproxylische Arten
              </h3>
              <p className="fs-5 text-shadow">
                Totholz, ob im Baum, stehend oder als abgestorbener Ast in der Krone,
                ist keine Gefahr, sondern eine Ressource. Über 1.300 Käferarten in Mitteleuropa
                sind obligatorisch auf Totholz angewiesen. Spechte schaffen Höhlen, die in
                den Folgejahren von Fledermäusen, Hohltauben und Raufußkäuzen genutzt werden.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={5}>
                  <img
                    src={Buehne2}
                    width={1200}
                    height={675}
                    alt="Baumpfleger im Korb einer Hubarbeitsbühne bei der Arbeit in der kahlen Krone einer Eiche"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Gezielte Eingriffe: Nur was sicherheitsrelevant ist, wird entfernt
                  </p>
                </Col>
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Totäste werden bei uns nicht reflexartig entfernt, sondern nach Position,
                    Gefährdungspotenzial und ökologischem Wert bewertet. Wo die Verkehrssicherheit
                    es zulässt, belassen wir Totholz bewusst im Baum: als Stehendtotholz,
                    als verkürzte Stümpfe oder als erhaltene Spechtbäume.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Die Vorgaben des § 44 BNatSchG fließen in jede Planung ein:
                    Eingriffe an besetzten Niststätten werden durch Vorabbegehung konsequent
                    ausgeschlossen. Anfallende Äste werden auf Wunsch vor Ort gehäckselt
                    und als natürlicher Mulch zurückgeführt, gemäß § 39 BNatSchG.
                  </p>
                </Col>
              </Row>

              <p className="fs-5 text-shadow mt-4">
                Sie planen Baumarbeiten und möchten dabei ökologische Werte erhalten? Wir beraten
                Sie individuell, ob Einzelbaum, Allee oder Bestand, ob Verkehrssicherungsschnitt,
                Kronenpflege oder Habitatbaumkonzept.
              </p>

              <div className="text-center mt-4">
                <Link to="/kontakt" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                  Baumpflege anfragen
                </Link>
              </div>

              <div className="ff-cross-links">
                <p>Weitere Leistungen von Forst &amp; Flur</p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <Link to="/mulchen" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Landschaftspflege
                  </Link>
                  <Link to="/baumfaellung" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Baumfällung
                  </Link>
                </div>
              </div>

            </Col>
          </Row>
        </Container>
      </div>

      {/* Fakten-Karussell */}
      <div className="hero-container text-white">
        <Container className="py-5">
          <Row className="justify-content-center text-center mb-4">
            <Col xs={12} lg={9}>
              <span className="ff-eyebrow">Wissenswertes</span>
              <h2 className="ff-section-title" style={{ marginBottom: ".5rem" }}>Gut zu wissen</h2>
              <p style={{ color: "var(--ff-muted)" }}>
                Fakten rund um Bäume, Naturschutz und Baumpflege
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12} lg={9}>
              <Carousel
                interval={karussellIntervall(6000)}
                indicators={true}
                prevLabel="Vorherige Folie"
                nextLabel="Nächste Folie"
                indicatorLabels={facts.map((_, i) => `Folie ${i + 1}`)}
              >
                {facts.map((fact, index) => (
                  <Carousel.Item key={index}>
                    <div
                      className="text-center px-3 px-md-5 pb-5"
                      style={{ minHeight: "240px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                    >
                      <div className="p-4 p-md-5 w-100" style={{ background: "rgba(7,26,16,.65)", borderRadius: "3px", border: "1px solid rgba(159,211,86,.15)" }}>
                        <h3 style={{ fontFamily: "var(--fh)", fontWeight: 800, fontSize: "1.5rem", textTransform: "uppercase", color: "var(--ff-grass)", marginBottom: "1rem" }}>
                          {fact.title}
                        </h3>
                        <p className="fs-5 text-white text-shadow mb-0">{fact.text}</p>
                      </div>
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default InfoBaumpflege;
